import { LeverageParameters } from '@katanaperps/katana-perps-sdk';
import { Reducer } from 'redux';

import { ApiMarket } from '#/api/types';
import { MarketSessionInfo } from '#/components/markets/intermittent/intermittentMarkets';
import { arraysEqual, setEqual } from '#/lib/utils';

import {
  debugUpdateMarketsStatus,
  loadHiddenMarket,
  loadMarkets,
} from './listedMarketsActions';
import { PerMarketStringMap } from './listedMarketsTypes';
import { calculateTickOrStepSize } from './listedMarketsUtils';

// Actions

type ListedMarketsAction = ReturnType<
  typeof loadMarkets | typeof loadHiddenMarket | typeof debugUpdateMarketsStatus
>;

// Reducer

export type TickSizes = {
  [market: string]:
    | {
        inPip: number;
        asDecimal: string;
        /** Note that 0 decimals is also valid */
        visibleDecimals: number;
      }
    | undefined;
};

export type StepSizes = {
  [market: string]:
    | {
        inPip: number;
        asDecimal: string;
        visibleDecimals: number;
      }
    | undefined;
};

type ListedMarketsState = {
  defaultMarket?: string;

  loadedForLoggedIn: null | boolean; // null - when not loaded at all

  // marketSymbol as "ETH-USDC"
  listedMarketsList: string[];
  listedTokensSet: null | Set<string>;

  /** Pinned markets will be displayed first on Tickers screen */
  marketsPinPosition?: {
    [market: string]: number;
  };

  marketsLeverageParams?: {
    [market: string]: LeverageParameters;
  };

  /** Margin type and intermittent market session info per market */
  marketsTradingInfo?: {
    [market: string]: MarketSessionInfo;
  };

  /**
   * Minimum price increment change in quote terms
   *
   * Per market tick size (Number of decimals for price),
   *  if not defined we fall back to full 8 decimals (default exchange decimals)
   * More https://docs.google.com/document/d/1RPJAuAxGR0s702ldW9LqlYvHUMo9Rox_lSRyJIZeH0k/edit
   */
  tickSizes: TickSizes;

  /**
   * Minimum quantity increment change in base terms
   *
   * Per market step size (Number of decimals for price),
   *  if not defined we fall back to full 8 decimals (default exchange decimals)
   * More https://docs.google.com/document/d/1RPJAuAxGR0s702ldW9LqlYvHUMo9Rox_lSRyJIZeH0k/edit
   */
  stepSizes: StepSizes;

  /** Minimum order size (in base) per market map */
  takerOrderMinimums: PerMarketStringMap;

  /** Maximum positions size (in base) per market map */
  maximumPositionSizes: PerMarketStringMap;

  /** Active trading markets @see {@link ApiMarket.status} */
  // enabledTradingMarkets: null | Set<string>;
  /** Markets with cancels enabled @see {@link ApiMarket.status} */
  // enabledCancelsMarkets: null | Set<string>;
  /** When a market’s status is limitMaker in the /markets endpoint, the Buy/Sell panel should go into post-only mode */
  // limitMakerOnlyMarketsSet: null | Set<string>;
};

const defaultState: ListedMarketsState = {
  defaultMarket: undefined,

  loadedForLoggedIn: null,

  listedMarketsList: [],
  listedTokensSet: null,

  marketsPinPosition: undefined,
  tickSizes: {},
  stepSizes: {},

  takerOrderMinimums: {},
  maximumPositionSizes: {},

  marketsLeverageParams: {},
  marketsTradingInfo: {},

  // enabledTradingMarkets: null,
  // enabledCancelsMarkets: null,
  // limitMakerOnlyMarketsSet: null,
};

function transformMarketLeverageParams(market: ApiMarket) {
  return {
    maximumPositionSize: market.maximumPositionSize,
    initialMarginFraction: market.initialMarginFraction,
    maintenanceMarginFraction: market.maintenanceMarginFraction,
    basePositionSize: market.basePositionSize,
    incrementalPositionSize: market.incrementalPositionSize,
    incrementalInitialMarginFraction: market.incrementalInitialMarginFraction,
  };
}

function transformMarketSessionInfo(market: ApiMarket): MarketSessionInfo {
  return {
    marginType: market.marginType,
    type: market.type,
    tradingSessionStatus: market.tradingSessionStatus,
    approximateNextCloseTime: market.approximateNextCloseTime,
    approximateNextOpenTime: market.approximateNextOpenTime,
  };
}

export const listedMarketsReducer: Reducer<
  ListedMarketsState,
  ListedMarketsAction
> = (state = defaultState, action): ListedMarketsState => {
  switch (action.type) {
    case 'LOAD_HIDDEN_MARKET': {
      return {
        ...state,
        maximumPositionSizes: {
          ...state.maximumPositionSizes,
          [action.market.market]: action.market.maximumPositionSize,
        },
        stepSizes: {
          ...state.stepSizes,
          [action.market.market]: calculateTickOrStepSize(
            action.market.stepSize
          ),
        },
        takerOrderMinimums: {
          ...state.takerOrderMinimums,
          [action.market.market]: action.market.takerOrderMinimum,
        },
        tickSizes: {
          ...state.tickSizes,
          [action.market.market]: calculateTickOrStepSize(
            action.market.tickSize
          ),
        },
        marketsLeverageParams: {
          ...state.marketsLeverageParams,
          [action.market.market]: transformMarketLeverageParams(action.market),
        },
        marketsTradingInfo: {
          ...state.marketsTradingInfo,
          [action.market.market]: transformMarketSessionInfo(action.market),
        },
      };
    }
    case 'LOAD_MARKETS': {
      const pinnedPositions: ListedMarketsState['marketsPinPosition'] = {};
      const tickSizes: ListedMarketsState['tickSizes'] = {
        // 'DIL-USD': calculateTickSize('0.00000001'),
        // 'DIL-USD': calculateTickSize('0.001'),
      };
      const stepSizes: ListedMarketsState['stepSizes'] = {};
      const takerOrderMinimums: ListedMarketsState['takerOrderMinimums'] = {};
      const maximumPositionSizes: ListedMarketsState['maximumPositionSizes'] =
        {};
      const marketsLeverageParams: ListedMarketsState['marketsLeverageParams'] =
        {};
      const marketsTradingInfo: ListedMarketsState['marketsTradingInfo'] = {};

      const newList = action.markets.map(market => {
        if (market.pinPosition) {
          pinnedPositions[market.market] = market.pinPosition;
        }
        // To prevent re-renders when loading
        // tickSizes[market.market] = calculateTickSize('0.00000001');
        if (market.tickSize) {
          tickSizes[market.market] = calculateTickOrStepSize(market.tickSize);
        }
        if (market.stepSize) {
          stepSizes[market.market] = calculateTickOrStepSize(market.stepSize);
        }
        takerOrderMinimums[market.market] = market.takerOrderMinimum;
        maximumPositionSizes[market.market] = market.maximumPositionSize;
        marketsLeverageParams[market.market] =
          transformMarketLeverageParams(market);
        marketsTradingInfo[market.market] = transformMarketSessionInfo(market);
        return market.market;
      });

      const newSet = new Set(
        (action.markets || [])
          .map(market => [market.baseAsset, market.quoteAsset])
          .flat()
      );
      return {
        defaultMarket: action.defaultMarket || '',
        loadedForLoggedIn: action.loggedIn,
        listedMarketsList: arraysEqual(newList, state.listedMarketsList)
          ? state.listedMarketsList
          : newList,
        listedTokensSet:
          state.listedTokensSet && setEqual(newSet, state.listedTokensSet)
            ? state.listedTokensSet
            : newSet,
        marketsPinPosition: pinnedPositions,
        tickSizes,
        stepSizes,
        takerOrderMinimums,
        maximumPositionSizes,
        marketsLeverageParams,
        marketsTradingInfo,
        // enabledTradingMarkets: new Set(
        //   action.markets
        //     .filter(
        //       market =>
        //         market.status !== 'inactive' && market.status !== 'cancelsOnly'
        //     )
        //     .map(market => market.market)
        // ),
        // enabledCancelsMarkets: new Set(
        //   action.markets
        //     .filter(market => market.status !== 'inactive')
        //     .map(market => market.market)
        // ),
        // limitMakerOnlyMarketsSet: new Set(
        //   action.markets
        //     .filter(market => market.status === 'limitMakerOnly')
        //     .map(market => market.market)
        // ),
      };
    }
    case 'DEBUG_UPDATE_LISTED_MARKETS_STATUS':
      return {
        ...state,
        // limitMakerOnlyMarketsSet: new Set(
        //   action.status === 'limitMakerOnly' ? state.listedMarketsList : []
        // ),
      };
    default:
      return state;
  }
};
